import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Keeps a value "active" while either the primary trigger is hovered
 * or the secondary target (e.g. popover) is hovered, with a configurable
 * delay before hiding to allow mouse travel between the two.
 */
function useHoverBridge<T>(
  hoverTarget: T | null,
  delayMs: number,
  onActivate?: (target: T) => void,
): [
  T | null,
  {
    onMouseEnter: () => void;
    onMouseMove: () => void;
    onMouseLeave: () => void;
  },
  () => void,
] {
  const [activeTarget, setActiveTarget] = useState<T | null>(null);
  const isSecondaryHovered = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout>>();

  const cancelTimer = useCallback(() => {
    if (timer.current !== undefined) {
      clearTimeout(timer.current);
      timer.current = undefined;
    }
  }, []);

  const scheduleHide = useCallback(() => {
    cancelTimer();
    timer.current = setTimeout(() => {
      setActiveTarget(null);
      timer.current = undefined;
    }, delayMs);
  }, [delayMs, cancelTimer]);

  useEffect(() => {
    if (hoverTarget !== null) {
      cancelTimer();
      setActiveTarget(hoverTarget);
      onActivate?.(hoverTarget);
    } else {
      scheduleHide();
    }
  }, [hoverTarget, cancelTimer, scheduleHide]);

  useEffect(() => cancelTimer, [cancelTimer]);

  const secondaryHandlers = useMemo(
    () => ({
      onMouseEnter: (): void => {
        isSecondaryHovered.current = true;
        cancelTimer();
      },
      onMouseMove: (): void => {
        if (isSecondaryHovered.current) {
          cancelTimer();
        }
      },
      onMouseLeave: (): void => {
        isSecondaryHovered.current = false;
        scheduleHide();
      },
    }),
    [cancelTimer, scheduleHide],
  );

  const reset = useCallback(() => {
    cancelTimer();
    isSecondaryHovered.current = false;
    setActiveTarget(null);
  }, [cancelTimer]);

  return [activeTarget, secondaryHandlers, reset];
}

export default useHoverBridge;
